import { useMemo } from 'react';
import { ChatContextItem } from './types';
import { providePageContext, useProvidePageContext, usePageContext, generateRegistrationId } from './page';

export interface Question {
  prompt: string;
  title?: string;
  context?: ChatContextItem[];
}

export interface QuestionRegistration {
  id: string;
  urlPattern: string | RegExp;
  questions: Question[];
}

/**
 * Registers questions for specific pages based on URL patterns.
 * Returns a setter function to update the questions dynamically, similar to useState.
 *
 * Multiple registrations with the same URL pattern are allowed and will all be merged
 * when the pattern matches. Each call creates a new independent registration.
 *
 * @param urlPattern - URL pattern (string or RegExp) to match against page URLs
 * @param initialQuestions - Initial array of questions to provide when the pattern matches
 * @returns A setter function to update the questions, with an unregister method attached
 */
export function provideQuestions(
  urlPattern: string | RegExp,
  initialQuestions: Question[]
): ((questions: Question[]) => void) & { unregister: () => void } {
  // Generate a unique registration ID to prevent collisions when multiple registrations exist
  const registrationId = generateRegistrationId('question');

  // Convert questions to ChatContextItem format for the underlying system
  const convertQuestionsToContext = (questions: Question[]): ChatContextItem[] => {
    return questions.map((question, index) => ({
      node: {
        id: `question-${registrationId}-${index}`,
        name: question.title || question.prompt,
        navigable: false,
        selectable: true,
        icon: 'question-circle',
        data: {
          type: 'question',
          prompt: question.prompt,
          title: question.title,
          context: question.context || [],
        },
      },
      occurrences: [],
    }));
  };

  // Use the existing providePageContext system
  const setContext = providePageContext(urlPattern, convertQuestionsToContext(initialQuestions));

  // Create a wrapper that converts between questions and context
  const setQuestions = (newQuestions: Question[]) => {
    setContext(convertQuestionsToContext(newQuestions));
  };

  // Add unregister method
  setQuestions.unregister = setContext.unregister;

  return setQuestions;
}

/**
 * React hook for providing questions that automatically cleans up on unmount.
 * This is the recommended way to use questions in React components.
 *
 * @param urlPattern - URL pattern (string or RegExp) to match against page URLs
 * @param initialQuestions - Initial array of questions to provide when the pattern matches
 * @returns A setter function to update the questions
 */
export function useProvideQuestions(
  urlPattern: string | RegExp,
  initialQuestions: Question[] = []
): (questions: Question[]) => void {
  // Generate a stable unique registration ID per component instance to prevent collisions
  const registrationId = useMemo(
    () => generateRegistrationId('question'),
    [] // Empty deps = generated once per component instance
  );

  // Convert questions to ChatContextItem format for the underlying system
  const convertQuestionsToContext = (questions: Question[]): ChatContextItem[] => {
    return questions.map((question, index) => ({
      node: {
        id: `question-${registrationId}-${index}`,
        name: question.title ?? question.prompt,
        navigable: false,
        selectable: true,
        icon: 'question-circle',
        data: {
          type: 'question',
          prompt: question.prompt,
          title: question.title,
          context: question.context || [],
        },
      },
      occurrences: [],
    }));
  };

  // Use the existing useProvidePageContext system
  const setContext = useProvidePageContext(urlPattern, convertQuestionsToContext(initialQuestions));

  // Create a wrapper that converts between questions and context
  const setQuestions = (newQuestions: Question[]) => {
    setContext(convertQuestionsToContext(newQuestions));
  };

  return setQuestions;
}

/**
 * React hook to get all questions that match the current URL.
 * This filters the page context to only return question-type items.
 *
 * @returns Array of questions from all matching registrations
 */
export function useQuestions(): Question[] {
  const pageContext = usePageContext({ allowQuestions: true, allowComponents: false });

  // Filter and convert context items to questions
  return convertContextToQuestions(pageContext);
}

const convertContextToQuestions = (contextItems: ChatContextItem[]): Question[] => {
  return contextItems
    .filter((item) => item.node.data?.type === 'question')
    .map((item) => ({
      prompt: item.node.data?.prompt || item.node.name,
      title: item.node.data?.title,
      context: item.node.data?.context || [],
    }));
};
